import { authFetch } from "../utils/fetchWithRefresh";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const billingRequest = {
	/* ──────────────────────────────────────────────
	    1) Setup Billing (create subscription in Paddle)
	   ────────────────────────────────────────────── */
	async billingSetup({ shopId, userId, accessToken, seats = 1 }) {
		if (!shopId) throw new Error("Missing shopId in billingSetup()");
		if (!userId) throw new Error("Missing userId in billingSetup()");
		if (!accessToken) throw new Error("Missing accessToken in billingSetup()");

		const res = await authFetch(`${API_BASE}/billing/setup/${shopId}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({
				userId,
				seats, // allows future multi-seat plans
			}),
		});

		if (!res.ok) {
			let errorMessage = await res.text().catch(() => "Unknown error");
			throw new Error(`Failed to setup billing: ${errorMessage}`);
		}

		return res.json();
	},


	/* ──────────────────────────────────────────────
	    1b) Reactivate a shop suspended for non-payment.
	    Returns { mode } where mode is one of:
	      "update_payment" → { updatePaymentUrl } — fix the card on Paddle
	      "checkout"       → { transactionId }    — subscription is gone, buy a new one
	      "recovered"      → already paid, shop just un-delisted
	      "not_suspended"  → nothing to do
	   ────────────────────────────────────────────── */
	async reactivate(shopId, accessToken) {
		if (!shopId) throw new Error("Missing shopId in reactivate()");
		if (!accessToken) throw new Error("Missing accessToken in reactivate()");

		const res = await authFetch(`${API_BASE}/billing/reactivate/${shopId}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
		});

		if (!res.ok) {
			const text = await res.text().catch(() => "Unknown error");
			throw new Error(`Failed to reactivate: ${text}`);
		}

		return res.json();
	},

	async startBilling(shopId, accessToken) {
		const res = await authFetch(`${API_BASE}/billing/launch/${shopId}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
		});

		if (!res.ok) {
			const text = await res.text();
			throw new Error(`Failed to start billing: ${text}`);
		}

		return res.json();
	},

	async billingStatus(shopId, accessToken) {
        if (!shopId) throw new Error("Missing shopId in billingStatus()");
        if (!accessToken) throw new Error("Missing accessToken in billingStatus()");

        const res = await authFetch(`${API_BASE}/billing/status/${shopId}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });

        if (!res.ok) {
            const text = await res.text().catch(() => "Unknown error");
            throw new Error(`Failed to fetch billing status: ${text}`);
        }

        return res.json();
        // {
        //   active: boolean,
        //   subscriptionActive: boolean,
        //   status: string | null,
        //   billingStatus: string | null
        // }
    },



	/* ──────────────────────────────────────────────
	5) Apply referral code
	────────────────────────────────────────────── */

	async getBillingInfo(shopId, accessToken) {
		const res = await authFetch(`${API_BASE}/billing/info/${shopId}`, {
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
		});

		if (!res.ok) {
			const text = await res.text();
			throw new Error(`Failed to fetch billing info: ${text}`);
		}

		return res.json(); // { billing: { customer, subscription, invoices } }
	},

	/* ──────────────────────────────────────────────
    6) Get Referral Dashboard (code + history)
   ────────────────────────────────────────────── */
	async getReferralDashboard(userId, accessToken) {
		const res = await authFetch(`${API_BASE}/referrals/dashboard/${userId}`, {
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
		});

		if (!res.ok) {
			const text = await res.text();
			throw new Error(`Failed to load referral dashboard: ${text}`);
		}

		return res.json();
		// { referralCode, referrals: [ ... ] }
	},

	/* ──────────────────────────────────────────────
    7) Generate Referral Code
   ────────────────────────────────────────────── */
	async generateReferralCode({ userId, accessToken }) {
		const res = await authFetch(`${API_BASE}/referrals/generate`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify({ userId }),
		});

		if (!res.ok) {
			const text = await res.text();
			throw new Error(`Failed to generate referral code: ${text}`);
		}

		return res.json();
		// { referralCode: "ABC123" }
	},
};

export default billingRequest;
